"use client";

import {
  ClockIcon,
  DollarSignIcon,
  FilterIcon,
  LayersIcon,
  ListChecks,
  Loader,
  PlaneIcon,
  RefreshCwIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
  type IFlightSelectOption,
  resetSelectOption,
  setFilters,
} from "@/feature/flight/search/state/slices/extract-flight-filter.slice";

const FilterDisplayPopover = () => {
  const dispatch = useAppDispatch();
  const selectOption = useAppSelector((state) => state.extractFlightFilter.selectOption);

  const loading = false;

  const [isOpen, setIsOpen] = useState(false);

  // Optimized filter counting using memoization
  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    // Skip counting 'CHEAPEST' since it's the default
    if (selectOption.type && selectOption.type !== "CHEAPEST") count++;
    if (selectOption.price_range?.length) count++;
    if (selectOption.airline?.length) count += selectOption.airline.length;
    if (selectOption.stoppage) {
      Object.values(selectOption.stoppage).forEach((stops) => {
        if (stops.length) count++;
      });
    }
    if (selectOption.refundable) count++;
    if (selectOption.aircraft?.length) count += selectOption.aircraft.length;
    if (selectOption.max_layover) count++;
    if (selectOption.min_layover) count++;
    if (selectOption.layover_city?.length)
      count += selectOption.layover_city.length;
    if (selectOption.timeFrame?.length) {
      count += selectOption.timeFrame.length;
    }
    if (selectOption.flight_number?.length)
      count += selectOption.flight_number.length;
    return count;
  }, [selectOption]);

  // Optimized filter removal
  const removeFilter = useCallback(
    (filterType: keyof IFlightSelectOption, value?: any) => {
      if (filterType === "type") {
        dispatch(setFilters({ [filterType]: "CHEAPEST" }));
      } else if (filterType === "price_range") {
        dispatch(setFilters({ [filterType]: [] }));
      } else if (Array.isArray(selectOption[filterType])) {
        const currentValues = [...(selectOption[filterType] as any[])];
        const updatedValues = currentValues.filter((v) => v !== value);
        dispatch(
          setFilters({
            [filterType]: updatedValues.length ? updatedValues : undefined,
          }),
        );
      } else if (filterType === "stoppage") {
        if (value) {
          const updatedStoppage = { ...selectOption.stoppage };
          delete updatedStoppage[value];
          dispatch(
            setFilters({
              stoppage: Object.keys(updatedStoppage).length
                ? updatedStoppage
                : undefined,
            }),
          );
        } else {
          dispatch(setFilters({ stoppage: undefined }));
        }
      } else if (filterType === "max_layover" || filterType === "min_layover") {
        dispatch(setFilters({ [filterType]: undefined }));
      }
    },
    [dispatch, selectOption],
  );

  // Reset all filters
  const handleResetAll = useCallback(() => {
    dispatch(resetSelectOption());
    setIsOpen(false);
  }, [dispatch]);

  // Grouped filter sections
  const filterSections = useMemo(() => {
    const sections = [];

    // Skip 'CHEAPEST' type as it's the default
    if (selectOption.type && selectOption.type !== "CHEAPEST") {
      sections.push({
        title: "Sorting",
        icon: <RefreshCwIcon className="w-3.5 h-3.5" />,
        items: [
          {
            label: `Sorted by: ${selectOption.type.toLowerCase()}`,
            onRemove: () => removeFilter("type"),
          },
        ],
      });
    }

    // Price Range
    if (selectOption.price_range?.length === 2) {
      sections.push({
        title: "Price Range",
        icon: <DollarSignIcon className="w-3.5 h-3.5" />,
        items: [
          {
            label: `$${selectOption.price_range[0]} - $${selectOption.price_range[1]}`,
            onRemove: () => removeFilter("price_range"),
          },
        ],
      });
    }

    // Airlines & Aircraft
    const flightDetails = [];
    if (selectOption.airline?.length) {
      selectOption.airline.forEach((airline) => {
        flightDetails.push({
          label: `Airline: ${airline}`,
          onRemove: () => removeFilter("airline", airline),
        });
      });
    }
    if (selectOption.aircraft?.length) {
      selectOption.aircraft.forEach((aircraft) => {
        flightDetails.push({
          label: `Aircraft: ${aircraft}`,
          onRemove: () => removeFilter("aircraft", aircraft),
        });
      });
    }
    if (selectOption.refundable) {
      flightDetails.push({
        label: "Refundable flights",
        onRemove: () => removeFilter("refundable"),
      });
    }
    if (flightDetails.length > 0) {
      sections.push({
        title: "Flight Details",
        icon: <PlaneIcon className="w-3.5 h-3.5" />,
        items: flightDetails,
      });
    }

    // Stops & Layovers
    const stopsAndLayovers = [];
    if (selectOption.stoppage) {
      Object.entries(selectOption.stoppage).forEach(([legIndex, stops]) => {
        if (stops.length > 0) {
          stopsAndLayovers.push({
            label: `Leg ${Number.parseInt(legIndex) + 1} stops: ${stops.join(", ")}`,
            onRemove: () => removeFilter("stoppage", Number.parseInt(legIndex)),
          });
        }
      });
    }
    if (selectOption.max_layover) {
      stopsAndLayovers.push({
        label: `Max layover: ${selectOption.max_layover.value}h (Leg ${selectOption.max_layover.index + 1})`,
        onRemove: () => removeFilter("max_layover"),
      });
    }
    if (selectOption.min_layover) {
      stopsAndLayovers.push({
        label: `Min layover: ${selectOption.min_layover.value}h (Leg ${selectOption.min_layover.index + 1})`,
        onRemove: () => removeFilter("min_layover"),
      });
    }
    if (selectOption.layover_city?.length) {
      selectOption.layover_city.forEach((city) => {
        stopsAndLayovers.push({
          label: `Layover city: ${city}`,
          onRemove: () => removeFilter("layover_city", city),
        });
      });
    }
    if (stopsAndLayovers.length > 0) {
      sections.push({
        title: "Stops & Layovers",
        icon: <LayersIcon className="w-3.5 h-3.5" />,
        items: stopsAndLayovers,
      });
    }

    // Time Frames
    if (selectOption.timeFrame?.length) {
      const timeFrames = selectOption.timeFrame.map((tf, index) => ({
        label: `${tf.type} at ${tf.code}: ${tf.time} (Leg ${tf.arr_index + 1})`,
        onRemove: () => {
          const updated = [...(selectOption.timeFrame || [])];
          updated.splice(index, 1);
          dispatch(
            setFilters({ timeFrame: updated.length ? updated : undefined }),
          );
        },
      }));
      sections.push({
        title: "Time Frames",
        icon: <ClockIcon className="w-3.5 h-3.5" />,
        items: timeFrames,
      });
    }

    // Flight Numbers
    if (selectOption.flight_number?.length) {
      const flightNumbers = selectOption.flight_number.map((number) => ({
        label: `Flight#${number}`,
        onRemove: () => removeFilter("flight_number", number),
      }));
      sections.push({
        title: "Flight Numbers",
        icon: <PlaneIcon className="w-3.5 h-3.5" />,
        items: flightNumbers,
      });
    }

    return sections;
  }, [selectOption, removeFilter, dispatch]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={appliedFiltersCount === 0 && !loading}>
        <Button variant="ghost" size="sm" className="mr-2">
          <ListChecks className="w-3.5 h-3.5" />
          {/* <span className="text-xs font-medium hidden md:block">Applied</span> */}
          <Badge
            className=" h-4 w-4 p-0 text-[10px] flex items-center justify-center"
            variant={"destructive"}
          >
            {loading ? (
              <Loader className="size-1 animate-spin" />
            ) : (
              appliedFiltersCount
            )}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        hidden={appliedFiltersCount === 0}
        className="w-72 p-0 bg-background border-border shadow-lg"
        align="end"
        sideOffset={4}
      >
        <div className="p-3 border-b border-border">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FilterIcon className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Applied Filters</h3>
            </div>
            <div className="flex items-center gap-1">
              {appliedFiltersCount > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetAll}
                  className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2Icon className="w-3 h-3" />
                  Clear All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 p-0"
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {appliedFiltersCount} filter{appliedFiltersCount !== 1 ? "s" : ""}{" "}
            applied
          </p>
        </div>

        {appliedFiltersCount === 0 ? (
          <div className="text-center py-6 px-4">
            <div className="bg-muted w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <FilterIcon className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="font-medium text-sm text-foreground">
              No filters applied
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Apply filters to narrow down your search
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto bg-muted">
            <div className="space-y-3">
              {filterSections.map((section, sectionIndex) => (
                <div
                  key={sectionIndex}
                  className="space-y-2  m-1.5 p-1.5 bg-background rounded-md border "
                >
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-muted-foreground">
                      {section.icon}
                    </span>
                    <h4 className="text-xs font-medium text-foreground">
                      {section.title}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {section.items.map((item, itemIndex) => (
                      <Badge
                        key={itemIndex}
                        variant="secondary"
                        className=" text-xs border-border transition-colors flex items-center  max-w-full px-1"
                      >
                        <span className="truncate max-w-48 text-xs">
                          {item.label}
                        </span>
                        <button
                          onClick={item.onRemove}
                          className="rounded-full  hover:bg-muted transition-colors flex-shrink-0 cursor-pointer "
                        >
                          <XIcon className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default FilterDisplayPopover;


